// Package Imports
import { NormalizedProduct } from '@/lib/types/products/normalizedProduct';
import { SearchProductSylius } from '@/lib/types/searchProductSylius';

// Function Imports
import { calculateCasePrice } from '@/lib/functions/calculateCasePrice';
import { calculateUnitPrice } from '@/lib/functions/calculateUnitPrice';
import { getDefaultImageForProduct } from '@/lib/functions/getDefaultImageForProduct';
import { getProductType } from '@/lib/functions/getProductType';
import { buildShippingDimensionsFromFields } from '@/lib/functions/resolveProductShippingDimensions';
import { resolveProductUrl } from '@/lib/functions/buildProductBreadcrumbs';

/**
 * Maps related product API response to ProductCard shape
 * Applies logic to null out price and case pricing if discount thresholds aren't met
 */
export const searchProductMapper = (api: SearchProductSylius): NormalizedProduct => {
  const productType = getProductType(api.slug);
  const imagePath = api.image ?? getDefaultImageForProduct(productType);

  // Calculate the unit pricing details
  const unitPriceInfo = calculateUnitPrice({
    price: api.price,
    promoPrice: api.promoPrice,
    promoPriceStartDate: api.promoPriceStartDate,
    promoPriceEndDate: api.promoPriceEndDate,
    keyClubDiscount: api.keyclubDiscount,
    keyClubDiscountStartDate: api.keyclubDiscountStartDate,
    keyClubDiscountEndDate: api.keyclubDiscountEndDate,
  });

  // Calculate the case pricing details
  const casePriceInfo = calculateCasePrice({
    price: api.price,
    casePrice: api.casePrice,
    caseQuantity: api.caseQuantity,
    promoCasePrice: api.promoCasePrice,
    promoPriceStartDate: api.promoPriceStartDate,
    promoPriceEndDate: api.promoPriceEndDate,
    keyClubCaseDiscount: api.keyclubCaseDiscount,
    keyClubDiscount: api.keyclubDiscount,
    keyClubDiscountStartDate: api.keyclubDiscountStartDate,
    keyClubDiscountEndDate: api.keyclubDiscountEndDate,
  });

  return {
    code: api.variantCode,
    details: {
      description: api.description,
      title: api.title,
      type: productType,
      attributes: {
        brand: api.brand,
        size: api.size,
      },
      image: imagePath,
    },
    url: resolveProductUrl(api.url, api.title, {
      slug: api.slug,
      description: api.description,
      taxonomy: api.taxonomy,
    }),
    pricing: {
      ...casePriceInfo,
      ...unitPriceInfo,
    },
    stock: {
      inStock: api.inStock,
      details: null,
    },
    taxonomy: api.taxonomy,
    shippingCategory: api.shippingCategory ?? null,
    shippingDimensions: buildShippingDimensionsFromFields(api),
  };
};
